import React, { useState, useEffect, useRef } from 'react';
import { Package, Clock, CheckCircle, XCircle, Truck, ShoppingBag, CreditCard, MapPin, Navigation, Wallet, Store, Building2, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useAuth } from '../context/AuthContext';
import { ordersAPI, bnplAPI, deliveryAPI, paymentsAPI, walletAPI } from '../lib/api';
import { formatCurrency, formatRelativeTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

// Leaflet icon fix
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const pickupIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
});

const dropoffIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
});

const driverIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
});

const STATUS_CONFIG = {
  pending_payment: { label: 'Pending Payment', color: 'text-amber-600 bg-amber-50 border-amber-200', icon: CreditCard },
  paid: { label: 'Paid', color: 'text-green-600 bg-green-50 border-green-200', icon: CheckCircle },
  confirmed: { label: 'Confirmed', color: 'text-blue-600 bg-blue-50 border-blue-200', icon: CheckCircle },
  processing: { label: 'Processing', color: 'text-violet-600 bg-violet-50 border-violet-200', icon: Package },
  shipped: { label: 'Shipped', color: 'text-cyan-600 bg-cyan-50 border-cyan-200', icon: Truck },
  delivered: { label: 'Delivered', color: 'text-green-600 bg-green-50 border-green-200', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'text-red-600 bg-red-50 border-red-200', icon: XCircle },
};

function DeliveryMap({ delivery }) {
  // Use simulated coordinates (NY area) for visualization
  const pickup = [40.7128 + Math.random() * 0.02, -74.006 + Math.random() * 0.02];
  const dropoff = [40.7580 + Math.random() * 0.02, -73.9855 + Math.random() * 0.02];
  const progress = delivery.status === 'delivered' ? 1 : delivery.status === 'picked_up' ? 0.6 : 0.3;
  const driverPos = [
    pickup[0] + (dropoff[0] - pickup[0]) * progress,
    pickup[1] + (dropoff[1] - pickup[1]) * progress,
  ];
  const center = [(pickup[0] + dropoff[0]) / 2, (pickup[1] + dropoff[1]) / 2];

  return (
    <div className="rounded-xl overflow-hidden border border-zinc-200 mt-3" style={{ height: 250 }} data-testid="delivery-map">
      <MapContainer center={center} zoom={13} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; OpenStreetMap'
        />
        <Marker position={pickup} icon={pickupIcon}><Popup>Pickup: Warehouse</Popup></Marker>
        <Marker position={dropoff} icon={dropoffIcon}><Popup>Dropoff: {delivery.dropoff_address || 'Customer'}</Popup></Marker>
        {delivery.status !== 'delivered' && (
          <Marker position={driverPos} icon={driverIcon}>
            <Popup>Driver {delivery.driver_name || 'En Route'}<br />ETA: ~{delivery.eta_minutes || 30} min</Popup>
          </Marker>
        )}
        <Polyline positions={[pickup, driverPos, dropoff]} color="#06b6d4" weight={3} dashArray="8" />
      </MapContainer>
    </div>
  );
}

function FundFlowMini({ order }) {
  const ff = order.fund_flow;
  if (!ff) return null;

  return (
    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
      className="mt-3 p-4 bg-zinc-50 rounded-xl border border-zinc-200 overflow-hidden" data-testid={`fund-flow-${order.id}`}>
      <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3 flex items-center gap-1">
        <Wallet size={12} /> Fund Distribution
      </p>
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-2 text-zinc-600"><Store size={14} className="text-violet-500" /> {ff.vendor_name || 'Vendor'}</span>
          <span className="font-mono font-bold text-violet-700">{formatCurrency(ff.vendor_receives)}</span>
        </div>
        {ff.delivery_fee > 0 && (
          <>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-zinc-600"><Truck size={14} className="text-cyan-500" /> OVA Driver (95%)</span>
              <span className="font-mono font-bold text-cyan-700">{formatCurrency(ff.driver_receives)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-zinc-600"><Building2 size={14} className="text-amber-500" /> MMarketing (5%)</span>
              <span className="font-mono font-bold text-amber-700">{formatCurrency(ff.platform_fee)}</span>
            </div>
          </>
        )}
        <div className="pt-2 border-t border-zinc-200 flex items-center justify-between text-sm font-semibold">
          <span className="text-zinc-700">Total</span>
          <span className="font-mono text-zinc-900">{formatCurrency(ff.total_paid)}</span>
        </div>
      </div>
    </motion.div>
  );
}

export default function OrdersPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bnplInstallments, setBnplInstallments] = useState('4');
  const [trackingData, setTrackingData] = useState({});
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [showFundFlow, setShowFundFlow] = useState({});
  const [walletBalance, setWalletBalance] = useState(0);
  const [payingWithWallet, setPayingWithWallet] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [oRes, wRes] = await Promise.all([
          ordersAPI.getAll(),
          walletAPI.getBalance().catch(() => ({ data: { balance: 0 } })),
        ]);
        setOrders(oRes.data);
        setWalletBalance(wRes.data.balance || 0);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handleCancel = async (orderId) => {
    try {
      await ordersAPI.cancel(orderId);
      setOrders(orders.map(o => o.id === orderId ? { ...o, status: 'cancelled' } : o));
      toast.success('Order cancelled');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleBNPL = async (orderId) => {
    try {
      const res = await bnplAPI.apply(orderId, parseInt(bnplInstallments));
      toast.success(res.data.message);
      const updated = await ordersAPI.getAll();
      setOrders(updated.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'BNPL failed'); }
  };

  const handlePay = async (orderId) => {
    try {
      const res = await paymentsAPI.createCheckout(orderId);
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
      else toast.error('Payment service unavailable');
    } catch (e) { toast.error(e.response?.data?.detail || 'Payment failed'); }
  };

  const handlePayWithWallet = async (orderId) => {
    setPayingWithWallet(orderId);
    try {
      const res = await walletAPI.payOrder(orderId);
      toast.success('Paid with wallet!');
      const updated = await ordersAPI.getAll();
      setOrders(updated.data);
      const wRes = await walletAPI.getBalance();
      setWalletBalance(wRes.data.balance || 0);
      // Show fund flow
      const order = updated.data.find(o => o.id === orderId);
      if (order?.fund_flow) {
        setShowFundFlow(prev => ({ ...prev, [orderId]: true }));
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Wallet payment failed'); }
    finally { setPayingWithWallet(null); }
  };

  const handleTrackDelivery = async (orderId) => {
    try {
      const res = await deliveryAPI.track(orderId);
      setTrackingData(prev => ({ ...prev, [orderId]: res.data }));
      setExpandedOrder(expandedOrder === orderId ? null : orderId);
    } catch (e) { toast.error('Tracking unavailable'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-zinc-50 pb-24">
      <section className="bg-white border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Package className="text-white" size={24} />
              </div>
              <div>
                <h1 className="font-display font-bold text-xl sm:text-2xl" data-testid="orders-title">My Orders</h1>
                <p className="text-zinc-500 text-sm">{orders.length} order{orders.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-zinc-100 rounded-full px-4 py-2" data-testid="wallet-balance-badge">
              <Wallet size={16} className="text-zinc-500" />
              <span className="text-sm font-mono font-bold text-zinc-900">{formatCurrency(walletBalance)}</span>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-4 sm:py-6 space-y-3 sm:space-y-4">
        {orders.length === 0 ? (
          <div className="text-center py-16 sm:py-20 bg-white rounded-2xl border border-zinc-200">
            <ShoppingBag className="mx-auto mb-4 text-zinc-300" size={48} />
            <h3 className="font-display text-xl text-zinc-900 mb-2">No Orders Yet</h3>
            <p className="text-zinc-500 mb-4 text-sm">Start shopping to see orders here</p>
            <Link to="/marketplace"><Button className="bg-zinc-900 text-white rounded-full">Browse Marketplace</Button></Link>
          </div>
        ) : orders.map((order, idx) => {
          const config = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending_payment;
          const Icon = config.icon;
          const delivery = trackingData[order.id];
          const isExpanded = expandedOrder === order.id;
          const hasFundFlow = order.fund_flow && showFundFlow[order.id];

          return (
            <motion.div key={order.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}
              className="bg-white border border-zinc-200 rounded-2xl overflow-hidden" data-testid={`order-${order.id}`}>
              <div className="p-4 sm:p-5">
                <div className="flex items-start sm:items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-2 rounded-lg border flex-shrink-0 ${config.color}`}><Icon size={18} /></div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-zinc-900 truncate text-sm sm:text-base">{order.product_name || order.service_name || `#${order.id.slice(0, 8)}`}</h3>
                      <p className="text-xs text-zinc-500">{formatRelativeTime(order.created_at)} {order.category && `· ${order.category}`}</p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-display font-bold text-base sm:text-lg text-zinc-900">{formatCurrency(order.total_price)}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${config.color}`}>{config.label}</span>
                  </div>
                </div>

                {/* Price breakdown when delivery */}
                {order.delivery_fee > 0 && (
                  <div className="flex gap-3 text-xs text-zinc-500 mb-2">
                    <span>Item: {formatCurrency(order.subtotal || order.total_price - order.delivery_fee)}</span>
                    <span>Delivery: {formatCurrency(order.delivery_fee)}</span>
                    {order.payment_method && <span className="ml-auto">Paid via {order.payment_method === 'wallet' ? 'Wallet' : 'Card'}</span>}
                  </div>
                )}

                {/* Tracking Progress */}
                {order.tracking && order.status !== 'cancelled' && (
                  <>
                    <div className="flex items-center gap-1 mt-3 mb-1">
                      {order.tracking.map((step, i) => (
                        <React.Fragment key={i}>
                          <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full flex-shrink-0 ${step.status === 'completed' ? 'bg-green-500' : 'bg-zinc-200'}`} />
                          {i < order.tracking.length - 1 && <div className={`flex-1 h-0.5 ${step.status === 'completed' ? 'bg-green-500' : 'bg-zinc-200'}`} />}
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="flex justify-between text-[9px] sm:text-[10px] text-zinc-400 mt-1">
                      {order.tracking.map((step, i) => <span key={i} className={`${step.status === 'completed' ? 'text-green-600 font-medium' : ''}`}>{step.step}</span>)}
                    </div>
                  </>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2 mt-3">
                  {order.status === 'pending_payment' && (
                    <>
                      <Button size="sm" onClick={() => handlePay(order.id)} className="bg-gradient-to-r from-[#b8ff00] to-[#00ff88] text-black text-xs rounded-full font-semibold" data-testid={`pay-btn-${order.id}`}>
                        <CreditCard size={12} className="mr-1" /> Pay with Card
                      </Button>
                      {walletBalance >= order.total_price && (
                        <Button size="sm" onClick={() => handlePayWithWallet(order.id)} disabled={payingWithWallet === order.id}
                          className="bg-zinc-900 text-white text-xs rounded-full font-semibold" data-testid={`wallet-pay-btn-${order.id}`}>
                          <Wallet size={12} className="mr-1" /> {payingWithWallet === order.id ? 'Paying...' : `Pay with Wallet (${formatCurrency(walletBalance)})`}
                        </Button>
                      )}
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="outline" className="text-xs rounded-full border-violet-300 text-violet-600" data-testid={`bnpl-btn-${order.id}`}>BNPL</Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader><DialogTitle>Buy Now Pay Later</DialogTitle></DialogHeader>
                          <p className="text-sm text-zinc-600 mb-3">Split <strong>{formatCurrency(order.total_price)}</strong> into easy installments</p>
                          <Select value={bnplInstallments} onValueChange={setBnplInstallments}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {[2, 3, 4, 6].map(n => <SelectItem key={n} value={String(n)}>{n} payments of {formatCurrency(order.total_price / n)}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Button onClick={() => handleBNPL(order.id)} className="w-full mt-3 bg-violet-600 text-white">Apply BNPL</Button>
                        </DialogContent>
                      </Dialog>
                      <Button size="sm" variant="outline" onClick={() => handleCancel(order.id)} className="text-xs rounded-full text-red-500 border-red-200">Cancel</Button>
                    </>
                  )}
                  {order.status === 'confirmed' && <Button size="sm" variant="outline" onClick={() => handleCancel(order.id)} className="text-xs rounded-full text-red-500 border-red-200">Cancel</Button>}

                  {['shipped', 'delivered', 'paid'].includes(order.status) && (
                    <Button size="sm" variant="outline" onClick={() => handleTrackDelivery(order.id)} className="text-xs rounded-full border-cyan-300 text-cyan-600" data-testid={`track-btn-${order.id}`}>
                      <Truck size={12} className="mr-1" /> {isExpanded ? 'Hide' : 'Track'} Delivery
                    </Button>
                  )}

                  {/* Fund Flow toggle for paid orders */}
                  {order.fund_flow && (
                    <Button size="sm" variant="outline" onClick={() => setShowFundFlow(prev => ({ ...prev, [order.id]: !prev[order.id] }))}
                      className="text-xs rounded-full border-emerald-300 text-emerald-600" data-testid={`fund-flow-btn-${order.id}`}>
                      <ArrowRight size={12} className="mr-1" /> {hasFundFlow ? 'Hide' : 'View'} Fund Flow
                    </Button>
                  )}

                  {order.tracking_number && <span className="text-xs text-zinc-400 flex items-center gap-1 ml-auto"><Truck size={12} /> {order.tracking_number}</span>}
                </div>

                {/* Fund Flow Expanded */}
                <AnimatePresence>
                  {hasFundFlow && <FundFlowMini order={order} />}
                </AnimatePresence>

                {/* Delivery Tracking Expanded */}
                <AnimatePresence>
                  {isExpanded && delivery && delivery.status !== 'no_delivery' && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      className="mt-4 pt-4 border-t border-zinc-100 overflow-hidden">
                      <div className="flex items-center gap-2 mb-3">
                        <Navigation size={16} className="text-cyan-500" />
                        <span className="text-sm font-semibold text-zinc-900">Live Delivery Tracking</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${delivery.status === 'delivered' ? 'bg-green-100 text-green-700' : 'bg-cyan-100 text-cyan-700'}`}>
                          {delivery.live_status || delivery.status?.replace('_', ' ')}
                        </span>
                      </div>

                      {/* Live Map */}
                      <DeliveryMap delivery={delivery} />

                      {/* Driver Info */}
                      {(delivery.driver_name || delivery.eta_minutes) && (
                        <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-xl mt-3">
                          <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center"><Truck className="text-cyan-600" size={18} /></div>
                          <div>
                            <p className="text-sm font-medium">{delivery.driver_name || 'Driver Assigned'}</p>
                            <p className="text-xs text-zinc-500">OVA Rideshare</p>
                          </div>
                          {delivery.eta_minutes && <p className="ml-auto text-sm font-display font-bold text-cyan-600">~{delivery.eta_minutes} min</p>}
                        </div>
                      )}

                      {/* Timeline */}
                      {delivery.timeline && delivery.timeline.length > 0 && (
                        <div className="space-y-2 mt-3">
                          {delivery.timeline.map((event, i) => (
                            <div key={i} className="flex items-start gap-3">
                              <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${i === delivery.timeline.length - 1 ? 'bg-cyan-500' : 'bg-zinc-300'}`} />
                              <div>
                                <p className="text-xs font-medium text-zinc-700">{event.message}</p>
                                <p className="text-[10px] text-zinc-400">{new Date(event.time).toLocaleString()}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center gap-2 mt-3 text-xs text-zinc-500">
                        <MapPin size={12} />
                        <span>Tracking: {delivery.tracking_number || order.tracking_number || 'N/A'}</span>
                        <span className="ml-auto">{delivery.provider === 'ova_rideshare' ? 'OVA Rideshare' : 'MMarketing Delivery'}</span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* No delivery message */}
                <AnimatePresence>
                  {isExpanded && delivery && delivery.status === 'no_delivery' && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      className="mt-3 p-3 bg-zinc-50 rounded-xl text-sm text-zinc-500 text-center overflow-hidden">
                      Delivery not yet dispatched. The vendor will ship your order soon.
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
